import React from "react";
//#region types
import { SubmitInputProps, SubmitInputFormProps } from "models/propTypes/inputPropTypes";
import { FileEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { MaxFileSize } from "utils/constants/fileConstants";
//#endregion
//#region components
import { FileUploaderContainer } from "./forms/baseFields/fileUploader";
import InputField from "./forms/baseFields/inputField";
import Icon from "./icon";
import { Grid, FormControl, CircularProgress, IconButton } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import * as Yup from "yup";
import clsx from "clsx";
import { UtilsHelper } from "utils/helpers";

const SubmitInput: React.FC<SubmitInputProps> = (props) => {
  // Refs, not render values: InputField skips re-renders while its value is unchanged, so it can
  // hold a key handler from an older render - one that would still see the input as disabled.
  const formikRef = React.useRef<FormikProps<SubmitInputFormProps> | null>(null);
  const disabledRef = React.useRef(props.disabled);
  disabledRef.current = props.disabled;
  const shiftKeyPressedRef = React.useRef<boolean>(false);

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    switch (e.key) {
      case "Enter":
        if (!formikRef.current || !props.submitOnEnter || shiftKeyPressedRef.current || disabledRef.current) break;

        e.preventDefault();
        formikRef.current.submitForm();
        break;
      case "Shift":
        shiftKeyPressedRef.current = true;
        break;
      default:
        break;
    }
  };

  const onKeyUpHandler = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    switch (e.key) {
      case "Shift":
        shiftKeyPressedRef.current = false;
        break;
      default:
        break;
    }
  };

  return (
    <Formik<SubmitInputFormProps>
      innerRef={formikRef}
      onSubmit={(values, { resetForm }) => {
        if (props.disabled) return;
        setIsSubmitting(true);

        // A chat composer clears on send, since the thread already shows the message; others clear on success.
        const clearNow = props.alwaysShowSubmitButton === true && !props.keepValueOnSubmit;
        if (clearNow) resetForm();

        props
          .handleSubmit(values.text, values.attachments)
          .then(() => {
            if (!clearNow && !props.keepValueOnSubmit) resetForm();
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      }}
      initialValues={{
        text: "",
        attachments: props.allowAttachments ? [] : undefined,
      }}
      validationSchema={() =>
        Yup.object<SubmitInputFormProps>({
          text: Yup.string().required(TranslationKeys.Validations.Required),
        })
      }
    >
      {({ values, setFieldValue, handleChange, errors, touched }) => {
        // An always-visible send button is disabled with nothing to send and while sending.
        const chatStyle = props.alwaysShowSubmitButton === true;
        const hasContent = !UtilsHelper.isEmpty(values.text) || !!values.attachments?.length;
        const submitDisabled = isSubmitting || !!props.disabled || (chatStyle && !hasContent);

        const onFilesUpdated = (uploadedFiles?: UploadedFile[]) => {
          setFieldValue("attachments", uploadedFiles);
        };

        const onCancelUpload = (index: number) => {
          if (index > -1) {
            const updatedFileList: UploadedFile[] = values.attachments ? [...values.attachments] : [];
            updatedFileList.splice(index, 1);

            setFieldValue("attachments", updatedFileList);
          }
        };

        return (
          <Grid item container xs={12} component={Form}>
            {props.allowAttachments && (
              <FileUploaderContainer
                fileUploaderType="inline"
                filesToUpload={values.attachments ?? []}
                onFilesUpdated={onFilesUpdated}
                onCancelUpload={onCancelUpload}
                allowAnyContentType={false}
                allowAnyImageType
                allowedTypes={[FileEnums.ContentTypes.ApplicationPDF]}
                maxFileSize={MaxFileSize}
              />
            )}

            <Grid item xs className={cssExports.submitInputContainer}>
              <Grid item xs>
                <FormControl fullWidth>
                  <InputField
                    rootClass={props.rootClass}
                    inputBaseProps={{
                      value: `${values.text}`,
                      name: "text",
                      onChange: handleChange,
                      multiline: true,
                      minRows: 1,
                      maxRows: 5,
                      onKeyDown: onKeyDownHandler,
                      onKeyUp: onKeyUpHandler,
                    }}
                    icon={props.icon}
                    label={props.floatingLabel ? props.label : undefined}
                    placeholder={props.label}
                  />
                </FormControl>
              </Grid>
              {chatStyle || hasContent ? (
                <Grid item className={cssExports.inputButton}>
                  <IconButton
                    className={clsx(cssExports.button, props.buttonClass)}
                    disabled={submitDisabled}
                    type={"submit"}
                  >
                    {/* A chat shows the wait in the thread, so no spinner here. */}
                    {isSubmitting && !chatStyle ? (
                      <CircularProgress size={25} />
                    ) : props.buttonIcon ? (
                      <Icon icon={props.buttonIcon} />
                    ) : (
                      props.buttonText
                    )}
                  </IconButton>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </Grid>
        );
      }}
    </Formik>
  );
};

export default SubmitInput;
